import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { IconButton, Typography, SvgIcon } from "@mui/material";
import Expand from "@mui/icons-material/ZoomOutMap";
import { mdiExpandAll, mdiCollapseAll } from "@mdi/js";
import uniq from "lodash/uniq";
import { shapeService } from "client-app-core";

import { CollectionCard, CollectionCardItem, Dialog } from "orion-components/CBComponents";
import { Translate, getTranslation } from "orion-components/i18n";
import { getWidgetState } from "orion-components/Profiles/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";
import { loadProfile } from "orion-components/ContextPanel/Actions";
import { userFeedsSelector } from "orion-components/GlobalData/Selectors";
import { getGlobalWidgetState } from "../Selectors";
import WidgetMenu from "./components/WidgetMenu";
import { setLocalAppState } from "orion-components/AppState/Actions";

const propTypes = {
    contextId: PropTypes.string,
    selectWidget: PropTypes.func,
    selected: PropTypes.bool,
    readOnly: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expanded: PropTypes.bool,
    widgetsExpandable: PropTypes.bool,
    updateEntity: PropTypes.func
};
const defaultProps = {
    updateEntity: () => { }
};
const widgetName = "alertingZones";

const AlertingZonesWidget = ({
    contextId,
    selectWidget,
    selected,
    readOnly,
    id,
    expanded,
    widgetsExpandable,
    updateEntity
}) => {
    const dispatch = useDispatch();

    const enabled = useSelector((state) => expanded || getWidgetState(state)(id, "enabled"));
    const userFeeds = useSelector((state) => userFeedsSelector(state) || []);
    const dir = useSelector((state) => getDir(state));
    const alertingZones = useSelector(state => state?.contextualData?.[contextId]?.entity?.alertingZones ?? []);
    const widgetState = useSelector((state) => getGlobalWidgetState(state)(widgetName)) || {};
    const disableProfile = useSelector((state) => state.appState.persisted?.disableProfile);
    const shapes = useSelector((state) => state.appState.persisted?.alertingZones || []);

    const [collapsed, setCollapsed] = useState(false);
    const [expandedChildren, setExpandedChildren] = useState([]);
    const [triggered, setTriggered] = useState(false);
    const [childKey, setChildKey] = useState(0); //Used for forcibly updating the  card child component when the same collapse value is required to be passed as a prop.
    const [items, setItems] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [dialog, setDialog] = useState(null);

    useEffect(() => {
        if (!shapes.length) {
            const types = ["polygon", "rectangle", "circle", "linestring"];
            shapeService.getMyShapes({ types }, (err, result) => {
                if (err) {
                    console.log(err, result);
                }
                if (result?.length > 0) {
                    // Using a redux state instead of a local state to avoid multiple requests to the server fetching shapes.
                    dispatch(setLocalAppState("alertingZones", result));
                }
            });
        }
    }, []);

    useEffect(() => {
        const selectedShapes = shapes?.filter((shape) => alertingZones.includes(shape.id));
        setItems(selectedShapes);
    }, [shapes, alertingZones]);

    const handleExpand = () => {
        dispatch(selectWidget(id));
    };

    const handleLoadEntityDetails = (item) => {
        dispatch(
            loadProfile(
                item.id,
                item.entityData.properties.overrideName || item.entityData.properties.name,
                item.entityType,
                "profile",
                "secondary"
            )
        );
    };

    const getCategories = () => {
        const itemIds = items ? items.map((item) => item.feedId) : [];
        const categories = userFeeds
            .filter((feed) => itemIds.includes(feed.feedId))
            .map((feed) => {
                const feedName = (feed.overrideName && feed.orgIntegrationName) || feed.name;
                return { name: feedName, id: feed.feedId };
            });
        return categories;
    };

    const categories = getCategories();
    const handleCollapseAndExpand = (listId, isExpanded) => {
        if (!triggered) {
            setTriggered(true);
        }
        if (expandedChildren.includes(listId) || !isExpanded) {
            const latestExpanded = expandedChildren.filter((value) => value !== listId);
            setExpandedChildren(latestExpanded);
        } else {
            if (!expandedChildren.includes(listId) && isExpanded) {
                setExpandedChildren([...expandedChildren, listId]);
            }
        }
    };

    useEffect(() => {
        if (triggered) {
            if (expandedChildren?.length > 0) {
                if (categories.length === expandedChildren.length) {
                    setCollapsed(false);
                }
            } else {
                if (!collapsed) {
                    setCollapsed(!collapsed);
                }
            }
        }
    }, [expandedChildren, triggered]);

    useEffect(() => {
        if (!collapsed) {
            const listChildren = categories?.map((element) => element.id);
            setExpandedChildren(listChildren);
        } else {
            setExpandedChildren([]);
        }
    }, [collapsed, items]);

    const styles = {
        svgIcon: {
            width: "20px",
            height: "20px",
            color: "#FCFDFD",
            cursor: "pointer",
            ...(dir === "rtl" && { marginLeft: "17px" }),
            ...(dir === "ltr" && { marginRight: "17px" })
        },
        header: {
            color: "#FFFFFF",
            fontFamily: "Roboto",
            fontSize: 14,
            textTransform: "capitalize"
        },
        note: {
            color: "#fff",
            fontFamily: "Roboto",
            marginTop: "8px",
            opacity: "0.69"
        }
    };

    const addAlertingZonesToEntity = (zoneIds) => {
        if (contextId && zoneIds?.length > 0) {
            const update = {
                alertingZones: uniq([...alertingZones, ...zoneIds])
            };
            dispatch(updateEntity(contextId, update, false));
        }
    };

    const handleCloseDialog = () => {
        setDialog(null);
        setSelectedEntity(null);
    };

    const removeAlertingZone = () => {
        if (!selectedEntity?.id) return;
        const update = { alertingZones: alertingZones?.filter((id) => id !== selectedEntity?.id) };

        dispatch(updateEntity(contextId, update, false));
        handleCloseDialog();
    };

    const openRemoveDialog = (id, geometry) => {
        setSelectedEntity({
            id,
            entityTypeTranslationKey: getTranslation(`global.profiles.widgets.alarmingZones.${geometry.type === "LineString"
                ? "line"
                : geometry.type === "Polygon"
                    ? "zone"
                    : "point"
                }`)
        });
        setDialog("remove-alerting-zone-dialog")
    };

    return selected || !enabled ? (
        <div />
    ) : (
        <div className="widget-wrapper collapsed" data-testid="alerting-zones-widget">
            {!expanded && (
                <div className="widget-header" data-testid="alerting-zones-widget-header">
                    <SvgIcon
                        style={styles.svgIcon}
                        onClick={() => {
                            setChildKey(childKey + 1);
                            setCollapsed(!collapsed);
                        }} data-testid="orion-components-profiles-widgets-alerting-zones-alerting-zones-widget-svg-icon"
                    >
                        <path d={collapsed ? mdiExpandAll : mdiCollapseAll} />
                    </SvgIcon>
                    <div className="cb-font-b2">
                        <Translate value="global.profiles.widgets.alarmingZones.title" />
                    </div>

                    <div className="widget-header-buttons" data-testid="orion-components-profiles-widgets-alerting-zones-alerting-zones-widget-div">
                        {widgetsExpandable ? (
                            <div className="widget-expand-button">
                                <IconButton
                                    style={{
                                        width: "auto",
                                        ...(dir === "rtl" && {
                                            paddingLeft: 0
                                        }),
                                        ...(dir === "ltr" && {
                                            paddingRight: 0
                                        })
                                    }}
                                    onClick={handleExpand} data-testid="orion-components-profiles-widgets-alerting-zones-alerting-zones-widget-icon-button"
                                >
                                    <Expand />
                                </IconButton>
                            </div>
                        ) : null}
                        <WidgetMenu
                            widgetName={id}
                            widgetState={widgetState}
                            expandedState={widgetState?.autoExpand}
                            dir={dir}
                            addAlertingZonesToEntity={addAlertingZonesToEntity}
                            alertingZones={alertingZones}
                            shapes={shapes}
                        />
                    </div>
                </div>
            )}
            <div className="widget-content" data-testid="alerting-zones-widget-content">
                {categories.length > 0 ? (
                    categories.map((category) => {
                        const { id, name } = category;
                        return (
                            <CollectionCard
                                key={id + childKey}
                                name={name}
                                dir={dir}
                                collapsed={collapsed}
                                id={id}
                                handleCollapseAndExpand={handleCollapseAndExpand}
                            >
                                {items
                                    .filter((item) => {
                                        return item.feedId === id;
                                    })
                                    .sort((a, b) => {
                                        return (a.entityData.properties.overrideName || a.entityData.properties.name) >
                                            (b.entityData.properties.overrideName || b.entityData.properties.name)
                                            ? 1
                                            : -1;
                                    })
                                    .map((item) => {
                                        const { entityData, feedId, id, isDeleted } = item;
                                        const { name, overrideName } = entityData.properties;
                                        const geometry = entityData.geometry;
                                        return (
                                            <CollectionCardItem
                                                canRemove={true}
                                                disabled={isDeleted}
                                                feedId={feedId}
                                                handleClick={
                                                    !disableProfile ? () => handleLoadEntityDetails(item) : null
                                                }
                                                handleRemove={() => openRemoveDialog(id, geometry)}
                                                id={id}
                                                key={id}
                                                name={overrideName || name}
                                                readOnly={readOnly}
                                                type={""}
                                                dir={dir}
                                                geometry={geometry}
                                                disableButtonTriggers={true}
                                            />
                                        );
                                    })}
                            </CollectionCard>
                        );
                    })
                ) : (
                    <Typography style={{ padding: 12, color: "#fff" }} align="center" variant="caption" component="p">
                        <Translate value="global.profiles.widgets.alarmingZones.noAssocEntities" />
                    </Typography>
                )}
                {dialog === "remove-alerting-zone-dialog" && <Dialog
                    open={dialog === "remove-alerting-zone-dialog"}
                    confirm={{
                        label: getTranslation("global.profiles.widgets.alarmingZones.remove"),
                        action: () => removeAlertingZone(),
                        style: { color: "#5594B9" }
                    }}
                    abort={{
                        label: getTranslation("global.profiles.widgets.alarmingZones.cancel"),
                        action: handleCloseDialog,
                        style: "#B4B9BF"
                    }}
                    dir={dir}
                    paperPropStyles={{
                        width: "607px",
                        padding: "1.5% 0.75%"
                    }} data-testid="orion-components-profiles-widgets-alerting-zones-alerting-zones-widget-dialog"
                >
                    <div>
                        <Typography fontSize="16px" style={styles.header}>
                            <Translate
                                value={"global.profiles.widgets.alarmingZones.removeAlarmingZone"}
                                count={selectedEntity?.entityTypeTranslationKey}
                            />
                        </Typography>

                        <Typography fontSize="11px" style={styles.note}>
                            <Translate value={"global.profiles.widgets.alarmingZones.note"} count={selectedEntity?.entityTypeTranslationKey} />
                        </Typography>
                    </div>
                </Dialog>}
            </div>
        </div>
    );
};

AlertingZonesWidget.propTypes = propTypes;
AlertingZonesWidget.defaultProps = defaultProps;

export default AlertingZonesWidget;
